/**
 * Centralized SEO primitive.
 *
 * Goals:
 *   - One place to emit consistent canonical, OG, Twitter, robots, JSON-LD.
 *   - Backward-compatible with the legacy `<SEO title description ogImage ogType twImage canonicalUrl />` shape.
 *   - Reasonable defaults focused on Clamphook's primary keywords (IOE, CSIT, NEB entrance preparation in Nepal).
 *   - First-class JSON-LD support so pages can ship Course / Breadcrumb / FAQ structured data with a single prop.
 */

import Head from "next/head";
import { useRouter } from "next/router";
import React from "react";

const SITE_URL = "https://clamphook.com";
const SITE_NAME = "Clamphook Academy";
const DEFAULT_LOCALE = "en_NP";
const DEFAULT_OG_IMAGE = "/img/Clamphook.png";
const TWITTER_HANDLE = "@clamphook";

const DEFAULTS = {
  title:
    "Best IOE Entrance Preparation Institute in Nepal | Clamphook Academy",
  description:
    "Prepare for IOE Entrance, BSc CSIT, NEB, and competitive exams with Clamphook Academy, Nepal's leading online and physical entrance preparation platform. Mock tests, expert mentors, and structured courses.",
  keywords: [
    "best IOE entrance preparation institute in Nepal",
    "IOE entrance preparation",
    "CSIT entrance preparation",
    "BSc CSIT entrance",
    "IOE mock test",
    "NEB to IOE",
    "Clamphook Academy",
    "entrance preparation Nepal",
    "Kathmandu IOE coaching",
  ],
};

const normalizeUrl = (url: string | undefined, fallbackPath = ""): string => {
  if (!url) return `${SITE_URL}${fallbackPath}`;
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url
      .replace("https://www.clamphook.com", SITE_URL)
      .replace("http://www.clamphook.com", SITE_URL)
      .replace("http://clamphook.com", SITE_URL);
  }
  if (url.startsWith("/")) return `${SITE_URL}${url}`;
  return `${SITE_URL}/${url}`;
};

/** Strip query strings, hash, and trailing slashes from a path so canonicals
 *  don't fragment SERP signals. Matches next.config.js trailingSlash: false. */
const stripQueryAndHash = (path: string): string => {
  const qIdx = path.indexOf("?");
  const hIdx = path.indexOf("#");
  let end = path.length;
  if (qIdx !== -1) end = Math.min(end, qIdx);
  if (hIdx !== -1) end = Math.min(end, hIdx);
  let clean = path.slice(0, end) || "/";
  // Enforce no trailing slash (except root "/")
  if (clean.length > 1 && clean.endsWith("/")) {
    clean = clean.slice(0, -1);
  }
  return clean;
};

export interface JsonLd {
  "@context"?: string;
  "@type": string;
  [key: string]: unknown;
}

export interface SEOProps {
  /** Page title. Falls back to a Clamphook IOE/CSIT default. */
  title?: string;
  /** Meta + OG description. */
  description?: string;
  /** Open Graph type. Default "website". */
  ogType?: string;
  /** Absolute or root-relative path to the OG image. */
  ogImage?: string;
  /** Twitter image (defaults to ogImage). */
  twImage?: string;
  /** Override the canonical URL. If omitted, computed from the current route. */
  canonicalUrl?: string;
  /** Set to true on private/auth-only/duplicate pages so they don't get indexed. */
  noindex?: boolean;
  /** Extra keywords appended to the default IOE/CSIT set. */
  keywords?: string[];
  /** One or more JSON-LD blocks. Use the helpers in `lib/seo/jsonLd.ts` to build them. */
  jsonLd?: JsonLd | JsonLd[];
  /** Optional published / updated timestamps for article-type pages. */
  publishedAt?: string;
  updatedAt?: string;
  /** Locale override; defaults to en_NP. */
  locale?: string;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  ogType = "website",
  ogImage,
  twImage,
  canonicalUrl,
  noindex = false,
  keywords,
  jsonLd,
  publishedAt,
  updatedAt,
  locale = DEFAULT_LOCALE,
}) => {
  const router = useRouter();
  const path = stripQueryAndHash(router?.asPath || "/");

  const resolvedTitle = (title || DEFAULTS.title).trim();
  const resolvedDescription = (description || DEFAULTS.description).trim();
  const resolvedOgImage = normalizeUrl(ogImage || DEFAULT_OG_IMAGE);
  const resolvedTwImage = normalizeUrl(twImage || ogImage || DEFAULT_OG_IMAGE);
  const canonical = normalizeUrl(canonicalUrl, path);

  const allKeywords = [
    ...DEFAULTS.keywords,
    ...(keywords || []),
  ].join(", ");

  const robots = noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";

  const jsonLdItems = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Head>
      <title>{resolvedTitle}</title>

      <meta name="description" content={resolvedDescription} />
      <meta name="keywords" content={allKeywords} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <meta name="author" content="Clamphook Academy" />
      <meta name="publisher" content="Clamphook Academy" />
      <meta name="application-name" content="Clamphook" />
      <meta name="apple-mobile-web-app-title" content="Clamphook" />

      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en-NP" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Local SEO geo-targeting */}
      <meta name="geo.region" content="NP-BA" />
      <meta name="geo.placename" content="Kathmandu" />
      <meta name="geo.position" content="27.7172;85.3240" />
      <meta name="ICBM" content="27.7172, 85.3240" />

      {/* Open Graph */}
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={locale} />
      <meta property="og:image" content={resolvedOgImage} key={resolvedOgImage} />
      <meta property="og:image:secure_url" content={resolvedOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={resolvedTitle} />

      {publishedAt && (
        <meta property="article:published_time" content={publishedAt} />
      )}
      {updatedAt && (
        <meta property="article:modified_time" content={updatedAt} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedTwImage} />
      <meta name="twitter:image:alt" content={resolvedTitle} />

      {/* JSON-LD structured data */}
      {jsonLdItems.map((item, i) => (
        <script
           
          key={`ld-${i}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({ "@context": "https://schema.org", ...item }),
          }}
        />
      ))}
    </Head>
  );
};

export default SEO;
export { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE };
